import { useEffect } from 'react';
import { CallAdapter, CallAdapterState } from '@azure/communication-react';
import { useApplicationInsights } from 'live-lesson/shared/util-insights-react';

export interface UseCallAdapterTelemetryProps {
    callAdapter: CallAdapter | undefined;
    lessonId: string;
}

/**
 * Hook to track Azure Communication Services call adapter state changes with Application Insights.
 * 
 * This hook automatically logs important call events like connection and disconnection,
 * providing consistent telemetry across student and moderator applications.
 * 
 * @param props Configuration object with callAdapter and lessonId
 * @example
 * ```tsx
 * useCallAdapterTelemetry({
 *     callAdapter,
 *     lessonId: props.lessonId
 * });
 * ```
 */
export const useCallAdapterTelemetry = ({
    callAdapter,
    lessonId
}: UseCallAdapterTelemetryProps): void => {
    const { trackUserAction, isInitialized } = useApplicationInsights();

    useEffect(() => {
        if (!callAdapter) return;

        const onStateChange = (state: CallAdapterState): void => {
            // Log call state changes to Application Insights
            if (isInitialized) {
                // Log specific events for important call states
                if (state.call?.state === 'Connected') {
                    trackUserAction('CallConnected', 'LiveLessonCall', {
                        lessonId,
                        callId: state.call.id,
                        participantCount: state.call.remoteParticipants?.['length'] || 0
                    });
                }

                if (state.endedCall) {
                    trackUserAction('CallEnded', 'LiveLessonCall', {
                        lessonId,
                        endReason: state.endedCall.callEndReason,
                        duration: (state.endedCall as any).callDuration || 0
                    });
                }
            }
        };

        callAdapter.onStateChange(onStateChange);

        return () => {
            callAdapter.offStateChange(onStateChange);
        };
    }, [callAdapter, isInitialized, trackUserAction, lessonId]);
};